import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { UserContext } from "../context/UserContext";
import { useReactToPrint } from "react-to-print";
import { Button, Modal } from "react-bootstrap";
import InvoiceEdit from "./InvoiceEdit";

const DocumentDetails = () => {
  const { user } = useContext(UserContext);
  const contentToPrint = useRef(null);

  const [invoice, setInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalInfo, setModalInfo] = useState({ title: "", body: "" });
  const [refresh, setRefresh] = useState(false);

  const { id } = useParams();

  const handlePrint = useReactToPrint({
    content: () => contentToPrint.current,
  });

  const AddressRenderer = (address) => {
    if (!address) return null;
    return address.split(",").map((part, i) => (
      <p key={i} style={{ margin: 0 }}>
        {part.trim()}
      </p>
    ));
  };

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/invoices/invoice/${id}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setInvoice(response.data.invoice);
        setInvoiceItems(response.data.invoiceItems);
      } catch (error) {
        console.error("Error fetching invoice:", error);
      }
    };

    fetchInvoiceDetails();
  }, [id, modalShow]);

  useEffect(() => {
    if (!invoice) return;

    const fetchCustomer = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/customers/customer/${invoice.CustomerId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCustomer(response.data);
      } catch (error) {
        console.error("Error fetching customer:", error);
      }
    };

    fetchCustomer();
  }, [invoice, refresh]);

  const paymentInfo = useMemo(() => {
    let saved = invoice?.paymentDetails;

    console.log("raw paymentDetails:", saved, "type:", typeof saved);

    if (typeof saved === "string") {
      try {
        saved = JSON.parse(saved);
      } catch (error) {
        console.error("Failed to parse paymentDetails:", error);
        saved = null;
      }
    }

    if (saved && typeof saved === "object") {
      return {
        bankName: saved.bankName || user?.bankName || "",
        accountName: saved.accountName || user?.bankAccName || "",
        accountNumber: saved.accountNumber || user?.bankAccNumber || "",
        IBAN: saved.IBAN || user?.bankAccIBAN || "",
        currency: saved.currency || user?.currency || "",
      };
    }

    return {
      bankName: user?.bankName || "",
      accountName: user?.bankAccName || "",
      accountNumber: user?.bankAccNumber || "",
      IBAN: user?.bankAccIBAN || "",
      currency: user?.currency || "",
    };
  }, [invoice, user]);

  if (!invoice || !customer) return null;

  const total = invoiceItems.reduce(
    (sum, item) => sum + Number(item.totalPrice),
    0,
  );

  function MyVerticallyCenteredModal(props) {
    return (
      <Modal {...props} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{props.modalInfo.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{props.modalInfo.body}</Modal.Body>
        <Modal.Footer>
          <Button onClick={props.onHide}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.actions}>
        <button className="btn btn-outline-dark" onClick={handlePrint}>
          Print
        </button>

        <button
          className="btn btn-dark"
          onClick={() => {
            setModalShow(true);
            setModalInfo({
              title: invoice.invoiceNumber,
              body: (
                <InvoiceEdit
                  invoice={invoice}
                  invoiceItems={invoiceItems}
                  setModalShow={setModalShow}
                  customer={customer}
                  setRefresh={setRefresh}
                />
              ),
            });
          }}
        >
          Edit
        </button>
      </div>

      <div style={styles.invoiceCard} ref={contentToPrint}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.businessName}>{user.businessName}</h2>
            {AddressRenderer(user.businessAddress)}
          </div>

          <div style={styles.invoiceRight}>
            <h1 style={styles.invoiceTitle}>INVOICE</h1>

            <div style={styles.invoiceMeta}>
              <p>
                <strong>Invoice #</strong> {invoice.invoiceNumber}
              </p>
              <p>
                <strong>Invoice date</strong>{" "}
                {new Date(invoice.date).toLocaleDateString()}
              </p>
              <p>
                <strong>Due date</strong> {invoice.dueDate}
              </p>
            </div>
          </div>
        </div>

        <div style={styles.billTo}>
          <h5 style={styles.redTitle}>Bill To</h5>
          <p style={{ fontWeight: 600 }}>{customer.name}</p>
          {AddressRenderer(customer.address)}
        </div>

        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeaderRow}>
              <th style={styles.th}>QTY</th>
              <th style={styles.th}>Description</th>
              <th style={styles.thRight}>Unit Price</th>
              <th style={styles.thRight}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoiceItems.map((item, index) => (
              <tr key={index} style={styles.tableRow}>
                <td style={styles.td}>{item.units}</td>
                <td style={styles.td}>{item.description}</td>
                <td style={styles.tdRight}>${item.unitPrice}</td>
                <td style={styles.tdRight}>${item.totalPrice}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={styles.totalSection}>
          <div style={styles.totalFinal}>
            <span>Total ({paymentInfo.currency || user.currency || ""})</span>
            <span>${total.toFixed(2)}</span>
          </div>
        </div>

        <div style={styles.terms}>
          <div style={styles.paymentCard}>
            <h5 style={styles.sectionTitle}>Payment Details</h5>
            <p>Bank: {paymentInfo.bankName}</p>
            <p>Account Name: {paymentInfo.accountName || "-"}</p>
            <p>Account Number: {paymentInfo.accountNumber || "-"}</p>
            <p>IBAN/SWIFT: {paymentInfo.IBAN || "-"}</p>
            <p>Currency: {paymentInfo.currency || "-"}</p>
          </div>
        </div>

        <MyVerticallyCenteredModal
          show={modalShow}
          onHide={() => setModalShow(false)}
          modalInfo={modalInfo}
        />
      </div>
    </div>
  );
};

/* ============================= */
/* STYLES                        */
/* ============================= */

const primaryRed = "#c4575b";

const styles = {
  page: {
    background: "#e9e9e9",
    padding: 40,
    fontFamily: "'Segoe UI', sans-serif",
    maxWidth: 900,
    margin: "40px auto",
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: 20,
    gap: 10,
  },

  invoiceCard: {
    background: "#ffffff",
    padding: 50,
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 40,
  },

  businessName: {
    margin: 0,
    fontSize: 22,
  },

  invoiceRight: {
    textAlign: "right",
  },

  invoiceTitle: {
    color: primaryRed,
    fontSize: 42,
    letterSpacing: 4,
    marginBottom: 20,
  },

  invoiceMeta: {
    lineHeight: 1,
  },

  billTo: {
    marginBottom: 30,
  },

  redTitle: {
    color: primaryRed,
    fontWeight: 600,
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 20,
  },

  tableHeaderRow: {
    background: primaryRed,
    color: "#fff",
  },

  th: {
    padding: 12,
    textAlign: "left",
  },

  thRight: {
    padding: 12,
    textAlign: "right",
  },

  tableRow: {
    borderBottom: "1px solid #ccc",
  },

  td: {
    padding: 12,
  },

  tdRight: {
    padding: 12,
    textAlign: "right",
  },

  totalSection: {
    marginTop: 30,
    maxWidth: 400,
    marginLeft: "auto",
  },

  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
  },

  totalFinal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "12px 0",
    marginTop: 10,
    borderTop: `2px solid ${primaryRed}`,
    borderBottom: `2px solid ${primaryRed}`,
    fontWeight: 700,
    color: primaryRed,
  },

  terms: {
    marginTop: 60,
  },

  paymentCard: {
    padding: 20,
    border: "1px solid #ddd",
    borderRadius: 8,
    maxWidth: 420,
  },

  sectionTitle: {
    color: primaryRed,
    marginBottom: 15,
  },
};

export default DocumentDetails;
